import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { View, ViewSchema } from "@common/types/views/views";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { mergeCachedView } from "@lib/mergeCachedView";
import { AccrualMatchColumn, MatchItemType } from "../types";
import { enforceLedgerColumns } from "../accrualMatchView";
import { buildAccrualMatchViewDefaults } from "../useAccrualMatchViewDefaults";
import { getDefaultAllocationView } from "./allocationView";

interface Params {
    isGlFirst: boolean;
    hasAccrualLedger: boolean;
    matchItemType: MatchItemType | null;
}

export function useAllocationView({
    isGlFirst,
    hasAccrualLedger,
    matchItemType,
}: Params) {
    const defaultAllocationView = useMemo(
        () => getDefaultAllocationView({ isGlFirst, hasAccrualLedger }),
        [isGlFirst, hasAccrualLedger],
    );
    const allocationColumnConfig = useMemo(
        () =>
            buildAccrualMatchViewDefaults({
                columnsVariant: "workspaceMatched",
                ledgerBasis: hasAccrualLedger
                    ? AccountingLedgerBasis.accruals
                    : AccountingLedgerBasis.cash,
                isGlFirst,
                matchItemType,
            }).viewConfig,
        [matchItemType, hasAccrualLedger, isGlFirst],
    );
    const [cachedAllocationView, setCachedAllocationView] =
        useWorkspaceUserCache<View>(
            WORKSPACE_USER_CACHE_KEYS.ACCRUAL_ALLOCATION_DRAWER_VIEW_CACHE_KEY,
            ViewSchema,
        );
    const [wasCashColumnOffered, setCashColumnOffered] =
        useWorkspaceUserCache<boolean>(
            WORKSPACE_USER_CACHE_KEYS.ACCRUAL_ALLOCATION_DRAWER_CASH_COLUMN_OFFERED_CACHE_KEY,
            z.boolean(),
        );
    const [wasAccrualColumnOffered, setAccrualColumnOffered] =
        useWorkspaceUserCache<boolean>(
            WORKSPACE_USER_CACHE_KEYS.ACCRUAL_ALLOCATION_DRAWER_ACCRUAL_COLUMN_OFFERED_CACHE_KEY,
            z.boolean(),
        );
    const cashColumnOfferedRef = useRef(wasCashColumnOffered === true);
    const accrualColumnOfferedRef = useRef(wasAccrualColumnOffered === true);
    const reconcileAllocationView = useCallback(
        (view: View | undefined) => {
            const merged = mergeCachedView(
                view,
                defaultAllocationView,
                allocationColumnConfig,
            );
            const showsAccrualLedger = defaultAllocationView.columns.some(
                (column) => column.id === AccrualMatchColumn.ACCRUAL_LEDGER,
            );
            const next = enforceLedgerColumns({
                view: merged,
                hasAccrualLedger: showsAccrualLedger,
                wasCashOffered: cashColumnOfferedRef.current,
                wasAccrualOffered: accrualColumnOfferedRef.current,
            });
            cashColumnOfferedRef.current = true;
            if (showsAccrualLedger) {
                accrualColumnOfferedRef.current = true;
            }
            return next;
        },
        [allocationColumnConfig, defaultAllocationView],
    );
    const [allocationView, setAllocationView] = useState<View>(() =>
        reconcileAllocationView(cachedAllocationView),
    );
    const lastDefaultViewRef = useRef(defaultAllocationView);
    useEffect(() => {
        if (defaultAllocationView === lastDefaultViewRef.current) {
            return;
        }
        lastDefaultViewRef.current = defaultAllocationView;
        setAllocationView((prev) => reconcileAllocationView(prev));
    }, [reconcileAllocationView, defaultAllocationView]);

    useEffect(() => {
        setCachedAllocationView(allocationView);
    }, [allocationView, setCachedAllocationView]);

    useEffect(() => {
        if (wasCashColumnOffered !== true) {
            setCashColumnOffered(true);
        }
    }, [wasCashColumnOffered, setCashColumnOffered]);

    useEffect(() => {
        if (hasAccrualLedger && wasAccrualColumnOffered !== true) {
            setAccrualColumnOffered(true);
        }
    }, [hasAccrualLedger, wasAccrualColumnOffered, setAccrualColumnOffered]);

    const setAllocationViewKeepingLedgerColumnsTogether = useCallback(
        (update: React.SetStateAction<View>) => {
            setAllocationView((prev) => {
                const next =
                    typeof update === "function" ? update(prev) : update;
                return enforceLedgerColumns({
                    view: next,
                    hasAccrualLedger,
                    wasCashOffered: true,
                    wasAccrualOffered: true,
                });
            });
        },
        [hasAccrualLedger],
    );

    return {
        allocationView,
        allocationColumnConfig,
        defaultAllocationView,
        setAllocationView: setAllocationViewKeepingLedgerColumnsTogether,
    };
}

export type AllocationViewState = ReturnType<typeof useAllocationView>;
